// src/pages/services/ProfessionalYear.jsx
import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import Tilt from "react-parallax-tilt";
import {
  FaChartLine,
  FaUserGraduate,
  FaClipboardCheck,
  FaCheckCircle,
  FaQuestionCircle,
  FaUniversity,
  FaLaptopCode,
  FaCogs,
} from "react-icons/fa";
import VisaDetailLayout from "../../components/VisaDetailLayout";

const fadeUp = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut", delay: i * 0.06 },
  }),
};

const WHY = [
  "Gain local work experience and industry mentoring",
  "Boost employability with practical, job-ready skills",
  "Earn up to 5 points toward PR under GSM (when eligible)",
];

const ELIGIBILITY = [
  "Completed an Australian degree (IT or Engineering)",
  "Hold or be eligible for a Temporary Graduate Visa (Subclass 485)",
  "Meet English proficiency requirements (per provider rules)",
];

const HOW = [
  "Match you to government-approved providers for your discipline",
  "Review documents and timelines; align with your visa dates",
  "Guide enrolment, internship placement, and next steps",
];

// Accounting removed
const PROVIDERS = [
  { name: "Performance Education", note: "IT" },
  { name: "Navitas Professional", note: "IT" },
  { name: "ACS PYear", note: "IT (ACS pathway)" },
  { name: "Engineering Education Australia", note: "Engineering (EA)" },
];

export default function ProfessionalYear() {
  const [discipline, setDiscipline] = useState("IT"); // IT | Engineering
  const [has485, setHas485] = useState(false);
  const [hasEnglish, setHasEnglish] = useState(false);
  const [hasAusDegree, setHasAusDegree] = useState(false);

  const ready = useMemo(() => has485 && hasEnglish && hasAusDegree, [
    has485,
    hasEnglish,
    hasAusDegree,
  ]);

  return (
    <VisaDetailLayout title="Professional Year Program">
      {/* Decorative gradient blobs */}
      <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-24 -left-24 h-80 w-80 rounded-full bg-blue-300/25 blur-3xl" />
        <div className="absolute -bottom-20 -right-24 h-96 w-96 rounded-full bg-indigo-300/25 blur-3xl" />
      </div>

      {/* Intro */}
      <motion.p
        variants={fadeUp}
        initial="hidden"
        animate="show"
        className="text-gray-700 text-lg mb-6"
      >
        The Professional Year (PY) Program helps international graduates in{" "}
        <strong>IT</strong> and <strong>Engineering</strong> gain practical skills,
        local workplace experience, and—in eligible cases—points toward skilled
        migration pathways.
      </motion.p>

      {/* Why / Eligibility / How (glass cards) */}
      <div className="grid gap-6 md:grid-cols-3">
        {[
          { icon: <FaChartLine />, title: "Why PY?", items: WHY, color: "from-blue-100 to-indigo-100" },
          { icon: <FaUserGraduate />, title: "Eligibility", items: ELIGIBILITY, color: "from-sky-100 to-blue-100" },
          { icon: <FaClipboardCheck />, title: "How We Help", items: HOW, color: "from-indigo-100 to-sky-100" },
        ].map((b, i) => (
          <motion.div
            key={b.title}
            variants={fadeUp}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-60px" }}
            custom={i}
          >
            <Tilt glareEnable glareMaxOpacity={0.15} glareColor="#60a5fa" glarePosition="all">
              <div className="rounded-2xl border border-white/60 bg-white/70 p-6 shadow-[0_6px_30px_rgba(0,0,0,0.06)] backdrop-blur hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(59,130,246,0.25)] transition">
                <div className={`mb-3 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br ${b.color} text-blue-700 ring-1 ring-blue-200`}>
                  <span className="text-xl">{b.icon}</span>
                </div>
                <h3 className="text-blue-900 font-semibold mb-2">{b.title}</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  {b.items.map((t) => (
                    <li key={t} className="flex items-start gap-2">
                      <FaCheckCircle className="mt-1 text-emerald-500" /> {t}
                    </li>
                  ))}
                </ul>
              </div>
            </Tilt>
          </motion.div>
        ))}
      </div>

      {/* Discipline tabs (Accounting removed) */}
      <div className="mt-8 flex flex-wrap items-center gap-3">
        {[
          { key: "IT", label: "Information Technology", icon: <FaLaptopCode /> },
          { key: "Engineering", label: "Engineering", icon: <FaCogs /> },
        ].map((d) => (
          <button
            key={d.key}
            onClick={() => setDiscipline(d.key)}
            className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition ${
              discipline === d.key
                ? "bg-blue-600 text-white shadow"
                : "bg-white text-blue-700 border hover:bg-blue-50"
            }`}
          >
            {d.icon} {d.label}
          </button>
        ))}
      </div>

      {/* Providers (hover glow cards) */}
      <section className="mt-6">
        <motion.h3
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="text-blue-800 font-semibold mb-3"
        >
          Popular Approved Providers
        </motion.h3>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {PROVIDERS.map((p, i) => (
            <motion.div
              key={p.name}
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              custom={i}
              className="group rounded-xl border bg-white/70 p-4 text-center shadow-sm backdrop-blur transition hover:-translate-y-1 hover:shadow-md"
            >
              <div className="text-lg font-semibold text-blue-900">{p.name}</div>
              <div className="mt-1 text-xs text-gray-600">{p.note}</div>
              <span className="mt-3 inline-block text-xs font-medium text-blue-700 group-hover:underline">
                Explore →
              </span>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Mini readiness checker */}
      <section className="mt-8">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="rounded-2xl border bg-white/80 p-6 shadow-sm backdrop-blur"
        >
          <div className="text-blue-800 font-semibold mb-4 flex items-center gap-2">
            <FaUniversity /> Quick Readiness Check
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <label className="flex items-center gap-3 text-sm text-gray-800">
              <input
                type="checkbox"
                checked={hasAusDegree}
                onChange={(e) => setHasAusDegree(e.target.checked)}
              />
              I completed an eligible Australian degree (IT/Engineering)
            </label>
            <label className="flex items-center gap-3 text-sm text-gray-800">
              <input
                type="checkbox"
                checked={has485}
                onChange={(e) => setHas485(e.target.checked)}
              />
              I hold / can obtain a Temporary Graduate (485) visa
            </label>
            <label className="flex items-center gap-3 text-sm text-gray-800">
              <input
                type="checkbox"
                checked={hasEnglish}
                onChange={(e) => setHasEnglish(e.target.checked)}
              />
              I meet the English requirement for my provider
            </label>
          </div>

          <div
            className={`mt-4 rounded-lg px-4 py-3 text-sm ${
              ready ? "bg-emerald-50 text-emerald-700" : "bg-blue-50 text-blue-800"
            }`}
          >
            {ready
              ? "Great! You look ready to start a Professional Year. We can shortlist providers and intake dates."
              : "Not sure yet? We’ll review your course, visa timing, and English to confirm the best pathway."}
          </div>
        </motion.div>
      </section>

      {/* FAQ */}
      <section className="mt-10">
        <motion.h3
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="text-blue-800 font-semibold mb-3"
        >
          FAQs
        </motion.h3>

        <div className="space-y-3">
          {[
            {
              q: "How long does a Professional Year take?",
              a: "Typically around 44–52 weeks depending on provider and internship placement length.",
            },
            {
              q: "Do all PY programs count for migration points?",
              a: "Only approved programs for your discipline may be eligible. We’ll confirm eligibility and the latest rules for you.",
            },
            {
              q: "Can I work while doing PY?",
              a: "Yes—most students combine PY with part-time work. We’ll help you plan around internship/visa conditions.",
            },
          ].map((item, i) => (
            <Accordion key={i} title={item.q} body={item.a} />
          ))}
        </div>
      </section>

      {/* CTA ribbon */}
      <section className="mt-12">
        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-blue-600 to-indigo-600 p-[1px]"
        >
          <div className="rounded-2xl bg-white/90 px-6 py-8 backdrop-blur">
            <div className="grid items-center gap-6 md:grid-cols-2">
              <div>
                <h4 className="text-2xl font-bold text-blue-900">
                  Level up your career with a Professional Year.
                </h4>
                <p className="mt-1 text-gray-600">
                  We’ll match you to providers, align timelines, and handle the admin.
                </p>
              </div>
              <div className="flex md:justify-end">
                <Link
                  to="/contact"
                  className="inline-flex items-center rounded-xl bg-blue-600 px-5 py-3 font-semibold text-white shadow hover:bg-blue-500"
                >
                  Talk to a Consultant
                </Link>
              </div>
            </div>
          </div>
        </motion.div>
      </section>
    </VisaDetailLayout>
  );
}

/* Small accordion */
function Accordion({ title, body }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-xl border bg-white/80 p-4 shadow-sm backdrop-blur">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between text-left"
      >
        <span className="font-medium text-blue-900 flex items-center gap-2">
          <FaQuestionCircle className="text-blue-600" /> {title}
        </span>
        <span className="text-blue-700">{open ? "–" : "+"}</span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.p
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="mt-2 text-gray-700 text-sm leading-relaxed"
          >
            {body}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}
